import type { PFFLetterGrade } from '../common/grade'
import type { FootballPlayer, FootballPlayerWithTeam } from '../football/football-player'
import type { SDKPlayerPropGradeAnalysis } from '../sdk'
import type { PlayerPropWithGradeAnalysis } from './player-prop'
import { PositionFilter } from '../football/player-positions'
import { NFLWeekAbbreviation } from '../football'
import { FootballStatKey } from '../football/football-stat'

export type Spread = number
export type OverUnder = number

export type SportsbookType = 'BETMGM' | 'FD' | string

export enum SportsbookName {
  CONSENSUS = 'Consensus',
  DRAFTKINGS = 'DraftKings',
  FANDUEL = 'FanDuel',
  CAESARS = 'Caesars',
  BETMGM = 'BetMGM',
  FANATICS = 'Fanatics',
}

export enum SportsbookAbbreviation {
  Consensus = 'SDIO_CONS',
  DraftKings = 'SDIO_DRAFTKINGS',
  FanDuel = 'SDIO_FANDUEL',
  BetMGM = 'SDIO_BETMGM',
  Caesars = 'SDIO_CAESARS',
  Fanatics = 'SDIO_FANATICS',
}

export const sportsbookNameToAbbrMap: Record<SportsbookName, SportsbookAbbreviation> = {
  [SportsbookName.CONSENSUS]: SportsbookAbbreviation.Consensus,
  [SportsbookName.DRAFTKINGS]: SportsbookAbbreviation.DraftKings,
  [SportsbookName.FANDUEL]: SportsbookAbbreviation.FanDuel,
  [SportsbookName.CAESARS]: SportsbookAbbreviation.Caesars,
  [SportsbookName.BETMGM]: SportsbookAbbreviation.BetMGM,
  [SportsbookName.FANATICS]: SportsbookAbbreviation.Fanatics,
}

export type GeneralTypeOfBet = 'Spread' | 'Moneyline' | 'Total' | 'Player Prop'

export type CategoryForBet = 'Game Bet' | 'Mismatches Prop' | 'General Player Prop'

export type SmartBetsTab = 'Games' | 'Player Props' | 'Best Bets' | 'Key Insights'

export type PlayerPropTab = 'Overview' | 'Matchup' | 'Hit Rate' | 'Odds'

export type DetailedEventViewWidgetType =
  | 'Popular'
  | 'TD Scorer Props'
  | 'Passing Props'
  | 'Rushing Props'
  | 'Receiving Props'

export type PlayerPropKey =
  | 'rush_att'
  | 'rush_yd'
  | 'rush_recv_yd'
  | 'pass_rush_yd'
  | 'pass_comp'
  | 'pass_int'
  | 'pass_att'
  | 'pass_yd'
  | 'pass_td'
  | 'recv_yd'
  | 'recv_rec'
  | 'longest_pass'
  | 'longest_reception'
  | 'kick_points'
  | 'tackle_assist'
  | 'first_team_td_scorer'
  | 'most_pass_yd'
  | 'most_rush_yd'
  | OutrightPlayerProps

// This is not an exhaustive list, really it should be every value from the PlayerPropKey type
// We should replace PlayerPropKey with an enum, but this would be too many changes to make right now.
// There is a tech debt ticket to fix this: https://profootballfocus.atlassian.net/browse/ED-314
export enum PlayerPropKeyEnum {
  PassingYards = 'pass_yd',
  PassAttempt = 'pass_att',
  PassCompletion = 'pass_comp',
  PassingTouchdown = 'pass_td',
  PassingInterceptions = 'pass_int',
  RushingYards = 'rush_yd',
  RushAttempts = 'rush_att',
  AnyTimeTouchdown = 'any_time_td',
  Receptions = 'recv_rec',
  ReceivingYards = 'recv_yd',
  FirstTouchdown = 'first_td',
  TwoPlusTouchdowns = 'two_plus_td',
  ThreePlusTouchdowns = 'three_plus_td',
  LastTouchdown = 'last_td',
  LongestReception = 'longest_reception',
  RushingAndReceivingYards = 'rush_recv_yd',
  LongestRush = 'longest_rush',
  PassingAndRushingYards = 'pass_rush_yd',
  LongestPass = 'longest_pass',
  MostPassingYards = 'most_pass_yd',
  MostRushingYards = 'most_rush_yd',
  FirstCompletion = 'first_completion',
  FirstReception = 'first_reception',
  FirstRushAttempt = 'first_rush_att',
  KickingPoints = 'kick_points',
  RecordASack = 'sack',
  TacklesPlusAssists = 'tackle_assist',
}

export type OutrightPlayerProps =
  | 'any_time_td'
  | 'first_td'
  | 'two_plus_td'
  | 'three_plus_td'
  | 'last_td'
  | 'first_half_td'
  | 'second_half_td'
  | 'first_td_second_half'
  | 'first_completion'
  | 'first_reception'
  | 'longest_rush'
  | 'first_rush_att'
  | 'sack'
  | 'alt_recv_yd'
  | 'alt_pass_yd'
  | 'alt_rush_yd'

export type DetailedTypeOfBet =
  | 'AwayTeamSpread'
  | 'AwayTeamMoneyline'
  | 'HomeTeamSpread'
  | 'HomeTeamMoneyline'
  | 'Player'
  | 'Over'
  | 'Under'
  | PlayerPropKey

export type ExtraBetMarketTypes =
  | OverUnderBetTypes
  | OutrightBetTypes
  | HandicapBetTypes
  | 'extra-points-made'
  | 'field-goals-made'
  | 'tie-final'
  | 'tie-first-quarter'
  | 'tie-second-quarter'
  | 'tie-third-quarter'
  | 'tie-fourth-quarter'

export type OverUnderBetTypes =
  | 'alt-total'
  | 'home-team-total'
  | 'away-team-total'
  | 'first-half-home-total'
  | 'second-half-home-total'
  | 'first-half-away-total'
  | 'second-half-away-total'
  | 'first-half-total'
  | 'second-half-total'
  | 'first-quarter-total'
  | 'second-quarter-total'
  | 'third-quarter-total'
  | 'fourth-quarter-total'
  | 'away-team-total-tds'
  | 'home-team-total-tds'
  | 'total-tds-first-half'
  | 'total-tds-second-half'

export type OutrightBetTypes =
  | 'correct-score'
  | 'first-quarter-winner'
  | 'second-quarter-winner'
  | 'third-quarter-winner'
  | 'fourth-quarter-winner'
  | 'first-quarter-winner-no-push'
  | 'second-quarter-winner-no-push'
  | 'third-quarter-winner-no-push'
  | 'fourth-quarter-winner-no-push'
  | 'first-quarter-points-odd-even'
  | 'second-quarter-points-odd-even'
  | 'third-quarter-points-odd-even'
  | 'fourth-quarter-points-odd-even'
  | 'first-quarter-both-score'
  | 'second-quarter-both-score'
  | 'third-quarter-both-score'
  | 'fourth-quarter-both-score'
  | 'score-every-quarter'
  | 'last-scoring-play'
  | 'winning-margin'
  | 'last-score-first-half'
  | 'team-to-score-first-half'
  | 'overtime'
  | 'firt-team-td-scorer'
  | 'race-to-five'
  | 'race-to-ten'
  | 'race-to-fifteen'
  | 'race-to-twenty'
  | 'race-to-twenty-five'
  | 'race-to-thirty'
  | 'teams-score-10'
  | 'teams-score-15'
  | 'teams-score-20'
  | 'teams-score-25'
  | 'teams-score-30'
  | 'teams-score-35'
  | 'teams-score-40'
  | 'first-quarter-td'
  | 'second-quarter-td'
  | 'third-quarter-td'
  | 'fourth-quarter-td'
  | 'moneyline-total-parlay'
  | 'any-time-td-moneyline-parlay'
  | 'first-td-moneyline-parlay'
  | 'first-half-final-winner-parlay'
  | 'teams-score-1-td-each-half'
  | 'teams-score-2-td-each-half'
  | 'teams-score-3-td-each-half'
  | 'highest-scoring-quarter'
  | 'highest-scoring-half'
  | 'team-first-fg'
  | 'game-special'

export type HandicapBetTypes = 'alt-handicap' | 'first-half-handicap' | 'second-half-handicap' | 'handicap-total-parlay'

export type BetStatus = 'winner' | 'loser' | 'void' | undefined

export type BetOptionType = 'over' | 'under' | 'home' | 'away' | 'yes' | 'no'

export interface EventMarketData {
  eventId: number
  market: string
  value: number | null
  odds: number
  option: BetOptionType
  sportsbookBetId: string
  sportsbookEventId: string
  sportsbookMarketId: string
  sportsbookOptionId: string
  status: BetStatus
  uniqueBetId: string
}

export interface BetProduct {
  value: Spread | OverUnder | string | number | null
  odds: number
  description?: string
  sportsbookBetId?: string
  sportsbookMarketId?: string
  sportsbookOptionId?: string
  updatedTimestamp?: number
}
export interface BetProductWithMarket extends BetProduct {
  edge?: number
  gameId?: number
  eventId?: number
  playerId?: number
  detailedType?: DetailedTypeOfBet
  generalType?: GeneralTypeOfBet
  category?: CategoryForBet
  valueType?: PlayerPropKey
}

export interface BetProductWithSportsbook extends BetProductWithMarket {
  status: BetStatus
  uniqueBetId: string
  sportsbookId: SportsbookType
  sportsbookEventId?: string
}

export interface PlayerProp {
  eventId?: number
  gameId: number
  playerId: number
  odds: number
  value: number | string
  name: string
  option: BetOptionType
  propKey: PlayerPropKey
  sportsbookId: string
  sportsbookBetId: string
  sportsbookEventId: string
  sportsbookMarketId: string
  sportsbookOptionId: string
  mismatchId?: string
  active?: boolean
  order?: number
  grade?: PFFLetterGrade
  edge?: number
  coverPercentage?: number
  valueType?: string
  status?: BetStatus
}

export interface PlayerPropsByWeekEvent {
  playerProps: PlayerProp[]
}

export type SDKPlayerProp = SDKPlayerPropGradeAnalysis &
  Omit<PlayerPropWithPlayer, 'grade'> &
  PlayerPropWithGradeAnalysis

export interface PlayerPropWithPlayer extends Omit<PlayerProp, 'playerId'> {
  playerId: PlayerProp['playerId'] // This is kept for backwards compatibilithy reasons
  player: Pick<FootballPlayer, 'playerId' | 'firstName' | 'lastName' | 'position' | 'teamId'>
}

export interface GreenlinePropProjection {
  gameId: number
  playerId: number
  propKey: string
  realValue: number
  sportsbookValue: number
  marketType: string
  projectionValue: number
  projectionPercentage: number
}

export interface GreenlineGamelineProjection {
  gameId: number
  sportsbookValue: number
  marketType: string
  projectionValue: number
  projectionPercentage: number
}

export enum InsightsOptions {
  BEST_BETS = 'Best Bets',
  MISMATCHES = 'Mismatches',
  TEAM_GRADING = 'Team Grading',
}

export enum MarketType {
  Total = 'total',
  Outright = 'outright',
  Handicap = 'handicap',
}

export enum OptionType {
  AwayTeam = 'away',
  HomeTeam = 'home',
  OverTotal = 'over',
  UnderTotal = 'under',
  OutrightYes = 'yes',
  OutrightNo = 'no',
}

export enum BetPropShortNames {
  ReceivingYds = 'receiving yds',
  RushingYds = 'rushing yds',
  TotalReceptions = 'total receptions',
  AnyTimeTouchdownScorer = 'any time touchdown scorer',
  PassAttempts = 'pass attempts',
  PassingTouchdowns = 'passing touchdowns',
  RushAttempts = 'rush attempts',
  PassCompletions = 'pass completions',
  PassingYds = 'passing yds',
  ToScore2PlusTouchdowns = 'to score 2+ touchdowns',
  ToScore3PlusTouchdowns = 'to score 3+ touchdowns',
  FirstTouchdownScorer = 'first touchdown scorer',
  LastTouchdownScorer = 'last touchdown scorer',
  LongestReception = 'longest reception',
  TotalRushingAndRecevingYards = 'total rushing and receiving yards',
  LongestRush = 'longest rush',
  PassingPlusRushingYds = 'passing + rushing yds',
  LongestPass = 'longest pass',
  MostPassingYardOfGame = 'most passing yards of game',
  MostRushingYardsOfGame = 'most rushing yards of game',
  FirstCompletion = 'first completion',
  FirstReception = 'first reception',
  FirstRushAttempt = 'first rush attempt',
  KickingPoints = 'kicking points',
  RecordASack = 'record a sack',
  TacklesPlusAssists = 'tackles + assists',
  PassingInterceptions = 'passing interceptions',
}

export interface SportsbookOption {
  type: BetOptionType // example: under
  value: number // example: 251.5
  payout: number // example: -110
  sportsbookId: SportsbookType // example: FD
  sportsbookEventId: string // example: 33818850 (identifies game/event at sportsbook)
  sportsbookMarketId: string // example: 468105857 (identifies bet market  at sportsbook Ex: Josh Allen receiving yards)
  sportsbookOptionId: string // example: 11889882 (identifies side of bet  at sportsbook Ex: O or U)
  status: string | null // win/loss Only currently works for FD
  cover: number // will be calculated in map, pick random between 0-100 until we ingest data from data science
  edge: number // https://profootballfocus.atlassian.net/wiki/spaces/~701214afbff96a49542d5a6c5f79d70c9e3fc/whiteboard/1162412033
  underHitRate: number
  overHitRate: number
  isSuggested: boolean
}

export interface StatRecordTeam {
  id: number
  city: string
  name: string
  abbreviation: string
}

export interface StatRecordGame {
  id: number
  season: number
  week: number
  weekAbbreviation: string
  league: string
  startTime: number | null
  homeTeam: StatRecordTeam
  awayTeam: StatRecordTeam
}

export interface StatOutcome {
  result: number
  game: StatRecordGame
}

export interface StatOutcomeSlim {
  result: number
  startTime: number
}

// used in game log table
export interface StatOutcomeWithConsensus extends StatOutcome {
  consensusLine: number | null
}

// used for projections and averages
export interface PlayerPropStats {
  passingYards: number | null
  passingAttempts: number | null
  passingCompletions: number | null
  passingTouchdowns: number | null
  passingInterceptions: number | null
  receivingYards: number | null
  receivingReceptions: number | null
  receivingTargets: number | null
  receivingTouchdowns: number | null
  rushingYards: number | null
  rushingAttempts: number | null
  rushingTouchdowns: number | null
  kickoffReturnTouchdowns: number | null
  puntReturnTouchdowns: number | null
}

export interface PlayerPropV2 {
  propKey: PlayerPropKeyEnum
  gameId: number
  options: SportsbookOption[]
  player: FootballPlayerWithTeam
  lastTen: StatOutcomeSlim[]
  averages: PlayerPropStats
  projections: PlayerPropStats
  matchup: number | null
}

export interface GetPlayerPropsUrlParams {
  league: string
  season: string
  weekAbbr: string
}

// Optional params will always be strings once encoded into the URL
export interface GetPlayerPropsOptionalParams {
  sportsbooks?: string // comma separated string
  propMarkets?: string // comma separated string
  positions?: string // comma separated string
  sortBy?: string
  gameIds?: string // comma separated string
  matchups?: string // comma separated string
  edgeMin?: string
  edgeMax?: string
  vigMin?: string
  vigMax?: string
  count?: string
  nextKey?: string
}

export interface GetPlayerPropsParams extends GetPlayerPropsUrlParams, GetPlayerPropsOptionalParams {}

export const MY_BOOKS = 'My Books'
export const BEST_ODDS = 'Best Odds'

export const SPORTSBOOK_LIST_BOOKS = [
  SportsbookName.CONSENSUS,
  SportsbookName.FANDUEL,
  SportsbookName.DRAFTKINGS,
  SportsbookName.CAESARS,
  SportsbookName.BETMGM,
  SportsbookName.FANATICS,
]

export const SPORTSBOOK_LIST = [BEST_ODDS, MY_BOOKS, ...SPORTSBOOK_LIST_BOOKS]

// Meant to be used on the FE to drive the filter options
export const FILTER_POSITIONS_LIST_DISPLAY: PositionFilter[] = [
  PositionFilter.QB,
  PositionFilter.RB,
  PositionFilter.WR,
  PositionFilter.TE,
]

// This seemed like the easiest way to get around the RB = HB & FB issue
// There are no players with position RB in the DB and the FE always shows RB
// so this will only be used on the BE as a default value
export const FILTER_POSITIONS_LIST_VALUES: PositionFilter[] = [
  PositionFilter.QB,
  PositionFilter.HB,
  PositionFilter.FB,
  PositionFilter.WR,
  PositionFilter.TE,
]

export enum GetPlayerPropsSortBy {
  CoverProbability = 'COVER_PROBABILITY',
  Edge = 'EDGE',
  OverHitRate = 'OVER_HIT_RATE',
  UnderHitRate = 'UNDER_HIT_RATE',
}

export enum MatchupScoreCategory {
  Great = 'great', // 4-5
  Good = 'good', // 3
  Poor = 'poor', // 0-2
}

export interface GetPlayerPropsParamsValidated {
  league: string
  season: string
  weekAbbr: NFLWeekAbbreviation
  sportsbooks: SportsbookAbbreviation[]
  propMarkets: PlayerPropKeyEnum[]
  positions: PositionFilter[]
  sortBy: GetPlayerPropsSortBy
  gameIds: number[]
  matchups: MatchupScoreCategory[]
  edgeMin: number
  edgeMax: number
  vigMin: number
  vigMax: number
  count: number
  nextKey?: string
}

export const MIN_MATCHUP = 0
export const MAX_MATCHUP = 5
export const MIN_EDGE = -40
export const MAX_EDGE = 40
export const MIN_VIG = -1000
export const MAX_VIG = 1000
export const DEFAULT_PROPS_PER_PAGE = 30
export const MIN_PROPS_PER_PAGE = 20
export const MAX_PROPS_PER_PAGE = 200
// These plus values are meant to be used on the BE to support ranges larger than the sliders allow
export const MIN_EDGE_PLUS = -100
export const MAX_EDGE_PLUS = 100
export const MIN_VIG_PLUS = -100000
export const MAX_VIG_PLUS = 100000

export const PROP_MARKET_LIST = [
  PlayerPropKeyEnum.AnyTimeTouchdown,
  PlayerPropKeyEnum.Receptions,
  PlayerPropKeyEnum.ReceivingYards,
  PlayerPropKeyEnum.RushAttempts,
  PlayerPropKeyEnum.RushingYards,
  PlayerPropKeyEnum.PassAttempt,
  PlayerPropKeyEnum.PassCompletion,
  PlayerPropKeyEnum.PassingInterceptions,
  PlayerPropKeyEnum.PassingTouchdown,
  PlayerPropKeyEnum.PassingYards,
]

export const STAT_KEY_LIST = [
  FootballStatKey.PassingYards,
  FootballStatKey.PassAttempt,
  FootballStatKey.PassCompletion,
  FootballStatKey.PassingTouchdown,
  FootballStatKey.PassingInterceptions,
  FootballStatKey.RushingYards,
  FootballStatKey.RushAttempts,
  FootballStatKey.AnyTimeTouchdown,
  FootballStatKey.Receptions,
  FootballStatKey.ReceivingYards,
]

export interface GetPlayerPropsByWeekResponse {
  playerProps: PlayerPropV2[]
  // null if we are on the last page
  nextKey: string | null
}

export interface GetPropHitRateParams {
  league: string
  season: string
  weekAbbr: string
  playerId: string
}

export interface HitRateRecordOutcome {
  result: number // this is the value as it relates to the given state key
  playerTeamId: number
  game: StatRecordGame
}

export interface HitRateResponse {
  consensusLine: number
  lastTenRecord: HitRateRecordOutcome[]
  h2hRecord: HitRateRecordOutcome[]
  similarDefRecord: HitRateRecordOutcome[]
}

export interface GetGameLogV2Params {
  league: string
  season: string
  playerId: string
}

export interface GameLogResponse {
  season: number
  gameLog: StatOutcomeWithConsensus[]
}

export interface CoverProjection {
  teamId: string
  team: string
  season: string
  week: string
  playerId: string
  playerName: string
  steps: number[]
  probability: string[]
}

export const PropMarketLabelMap: Partial<Record<PlayerPropKeyEnum, string>> = {
  [PlayerPropKeyEnum.PassingYards]: 'Pass Yds',
  [PlayerPropKeyEnum.PassingTouchdown]: 'Pass TDs',
  [PlayerPropKeyEnum.PassCompletion]: 'Pass CMPs',
  [PlayerPropKeyEnum.PassAttempt]: 'Pass ATTs',
  [PlayerPropKeyEnum.PassingInterceptions]: 'INTs',
  [PlayerPropKeyEnum.RushingYards]: 'Rush Yds',
  [PlayerPropKeyEnum.RushAttempts]: 'Rush ATTs',
  [PlayerPropKeyEnum.Receptions]: 'Recs',
  [PlayerPropKeyEnum.ReceivingYards]: 'Rec Yds',
  [PlayerPropKeyEnum.AnyTimeTouchdown]: 'Anytime TD',
}
